/**
 * supervisorOverrideRoles — who counts as a supervisor for an override
 * (BACKLOG-1787, Bill 2026-10-16).
 *
 * One admin setting decides it everywhere: SystemSetting
 * `override.supervisor.roles` (seed 408), a JSON array of ROP role codes,
 * default ["Supervisor"] because the Online Applicants legacy procedure
 * (pp.2-3) names a supervisor. An admin can add Manager etc. in the admin
 * panel. Mirrors SupervisorOverrideRules.ParseRoles / HoldsAllowedRole in
 * pcb-retail-api exactly: exact role-code match (case-insensitive), and
 * SuperAdmin is NOT implied. The server is the enforcement; this is only so the
 * screen shows the same answer.
 *
 * Pure — no React imports.
 */

export const SUPERVISOR_OVERRIDE_ROLES_SETTING = 'override.supervisor.roles';
export const DEFAULT_SUPERVISOR_OVERRIDE_ROLES: ReadonlyArray<string> = ['Supervisor'];

/** Parse the setting (JSON array, or comma-separated). Missing / blank / unreadable → the default. */
export function parseSupervisorOverrideRoles(raw: unknown): ReadonlyArray<string> {
  let list: unknown[] = [];
  if (Array.isArray(raw)) {
    list = raw;
  } else if (typeof raw === 'string' && raw.trim() !== '') {
    const text = raw.trim();
    if (text.startsWith('[')) {
      try {
        const parsed: unknown = JSON.parse(text);
        if (!Array.isArray(parsed)) return DEFAULT_SUPERVISOR_OVERRIDE_ROLES;
        list = parsed;
      } catch {
        return DEFAULT_SUPERVISOR_OVERRIDE_ROLES;
      }
    } else {
      list = text.split(',');
    }
  }
  const seen = new Set<string>();
  const out: string[] = [];
  for (const v of list) {
    if (typeof v !== 'string') continue;
    const t = v.trim();
    if (t === '' || seen.has(t.toLowerCase())) continue;
    seen.add(t.toLowerCase());
    out.push(t);
  }
  return out.length > 0 ? out : DEFAULT_SUPERVISOR_OVERRIDE_ROLES;
}

/** The allowed roles from a config bundle's systemSettings map. */
export function supervisorOverrideRolesFrom(systemSettings: unknown): ReadonlyArray<string> {
  if (!systemSettings || typeof systemSettings !== 'object') return DEFAULT_SUPERVISOR_OVERRIDE_ROLES;
  return parseSupervisorOverrideRoles((systemSettings as Record<string, unknown>)[SUPERVISOR_OVERRIDE_ROLES_SETTING]);
}

/** True when any of the user's role codes is an allowed role (exact, case-insensitive). */
export function holdsSupervisorOverrideRole(
  userRoles: ReadonlyArray<unknown> | undefined,
  allowed: ReadonlyArray<string>,
): boolean {
  if (!userRoles || userRoles.length === 0) return false;
  const set = new Set(allowed.map((r) => r.toLowerCase()));
  return userRoles.some((r) => typeof r === 'string' && set.has(r.trim().toLowerCase()));
}

/** "Supervisor", "Supervisor or Manager", "Supervisor, Manager or Executive". */
export function joinRoleNames(roles: ReadonlyArray<string>): string {
  if (roles.length === 0) return 'supervisor';
  if (roles.length === 1) return roles[0] ?? 'supervisor';
  return `${roles.slice(0, -1).join(', ')} or ${roles[roles.length - 1]}`;
}
